import { useState } from 'react';
import { Users, Check, Clock, MapPin, ChevronDown, Gauge } from 'lucide-react';
import { getEloLevel, getTournamentType } from '../../../engine';
import { BookingPaymentModal } from '../../booking/BookingPaymentModal';
import {
  openActivities,
  memberRegistration,
  activityCapacity,
  supportsIndividualSignup,
  activityWhenLabel,
  activityVenue,
} from '../memberData';
import {
  activitySignupIsServerAuthoritative,
  registerForActivity,
  unregisterFromActivity,
} from '../../../lib/api/activities';
import { saveWithRetry } from '../../../lib/saveWithRetry';
import { Card, EmptyState, SectionTitle } from '../mobileUi';
import { useT } from '../../../i18n';

export interface MobileActivitiesProps {
  member: any;
  activities: any[];
  /** Calendar bookings — an activity's time comes from its booked courts first. */
  bookings?: any[];
  clubInfo?: any;
  /** Only show activities at this centre (null = all). */
  centerFilter?: string | null;
  centerName?: string | null;
  onOpenCenters?: () => void;
  /** useActivities().persistActivities — the same path ActivitiesView uses. */
  persistActivities: (next: any[]) => any;
  /**
   * Adopt the activity the server returned (#245). REQUIRED, not optional: the
   * command has already committed, so a caller that does not wire this leaves
   * the member looking at state the server has moved past — and an optional
   * callback nobody passes is exactly that failure, silently.
   */
  applyServerActivity: (updated: any) => void;
  /** Inside a centre page: no heading, no centre chip. */
  embedded?: boolean;
}

// «Aktiviteter» — browse what's open and sign up / off.
//
// v1 handles INDIVIDUAL sign-up: confirmed while there is room, otherwise the
// waitlist, and paid activities go through the (mock) payment terminal first.
// Pair-, KM- and club-based formats need a partner/class/club; they are listed
// but hand over to the full activity page. The write goes through
// persistActivities, exactly like ActivitiesView's own mutate().
export function MobileActivities({
  member,
  activities,
  bookings = [],
  clubInfo = null,
  centerFilter = null,
  centerName = null,
  onOpenCenters,
  persistActivities,
  applyServerActivity,
  embedded = false,
}: MobileActivitiesProps) {
  const all = openActivities(activities || [], bookings);
  const atCenter = centerFilter
    ? all.filter((a: any) => activityVenue(a, clubInfo) === centerFilter)
    : all;
  const memberLevel = getEloLevel(Math.round(member?.elo || 0)).level;
  const [payFor, setPayFor] = useState<any>(null);
  const [busyId, setBusyId] = useState<any>(null);
  // «Passer meg»: hide what the member cannot sign up for (level, format).
  const [onlyEligible, setOnlyEligible] = useState(false);

  const levelOk = (a: any) => {
    const min = a.config?.levelMin;
    const max = a.config?.levelMax;
    if (min != null && memberLevel < Number(min)) return false;
    if (max != null && memberLevel > Number(max)) return false;
    return true;
  };
  const eligible = (a: any) => levelOk(a) && supportsIndividualSignup(a);
  const list = onlyEligible ? atCenter.filter(eligible) : atCenter;
  const hiddenCount = atCenter.length - atCenter.filter(eligible).length;

  // ── SAVING, UNDER THE #276 RULE ──────────────────────────────────
  //
  // A short retry stays invisible; once a save becomes NOTICEABLE the member
  // sees it is working, and if it ultimately fails they get a sentence they can
  // act on. `error` is that sentence — this screen had no way to report a
  // failure at all before, which was safe only because the old write path
  // swallowed everything.
  const t = useT();
  const [error, setError] = useState<string | null>(null);
  const [slowId, setSlowId] = useState<any>(null);

  const runSave = async (id: any, save: () => Promise<unknown>) => {
    setBusyId(id);
    setError(null);
    try {
      await saveWithRetry(save, { onSlow: () => setSlowId(id) });
    } catch (e: any) {
      setError(e?.message || t('mobile.activities.saveFailed'));
    } finally {
      setBusyId(null);
      setSlowId(null);
    }
  };

  /** The legacy blob path — unchanged; what the in-memory backend runs. */
  const mutate = async (id: any, fn: (a: any) => any) => {
    const next = (activities || []).map((a: any) => (a.id === id ? fn(a) : a));
    await runSave(id, () => persistActivities(next));
  };

  const signUp = (a: any, payment: any = null) => {
    if (!member) return Promise.resolve();
    // SERVER-AUTHORITATIVE PATH (#245). Nothing is decided here: the server
    // says who this is, whether there is room, and what it costs.
    if (activitySignupIsServerAuthoritative()) {
      return runSave(a.id, async () => {
        // The command returns the activity as it NOW is. Adopting that is the
        // point: the server decided the status, the rating and the price, so
        // recomputing any of it here would be a second opinion.
        const { activity } = await registerForActivity(String(a.id));
        applyServerActivity(activity);
      });
    }
    return mutate(a.id, (cur) => {
      if (memberRegistration(cur, member.id)) return cur; // already on it
      const { confirmed, max } = activityCapacity(cur);
      const status = max == null || confirmed < max ? 'confirmed' : 'waitlist';
      const price = Number(cur.config?.pricePerPlayer) || 0;
      const reg: any = {
        playerId: member.id,
        status,
        paidStatus: price > 0 ? 'paid' : 'free',
        signedUpAt: Date.now(),
        playerEloPre: Math.round(member.elo || 0),
      };
      if (payment) reg.payment = payment;
      return { ...cur, registrations: [...(cur.registrations || []), reg] };
    });
  };

  const signOff = (a: any) => {
    if (!member) return Promise.resolve();
    if (activitySignupIsServerAuthoritative()) {
      return runSave(a.id, async () => {
        const { activity } = await unregisterFromActivity(String(a.id));
        applyServerActivity(activity);
      });
    }
    return mutate(a.id, (cur) => ({
      ...cur,
      registrations: (cur.registrations || []).filter(
        (r: any) =>
          String(r.playerId) !== String(member.id) && String(r.partnerId) !== String(member.id),
      ),
    }));
  };

  const onSignUpClick = (a: any) => {
    const price = Number(a.config?.pricePerPlayer) || 0;
    if (price <= 0) return signUp(a);

    // NO MOCK PAYMENT ON THE SERVER PATH (#233).
    //
    // The terminal behind `setPayFor` is a mock: it always succeeds. On the
    // blob path it then wrote `paidStatus: 'paid'`, which was at least
    // self-consistent — a pretend payment producing a pretend record.
    //
    // The server will not write `paid` without a payment to point at, so the
    // same flow here would show the member a payment that "succeeded" and then
    // register them as UNPAID. That is worse than either half alone: the member
    // believes they have paid, the club sees that they have not, and nothing
    // reconciles the two.
    //
    // So priced sign-up is refused until payment is real (#283/#284/#285).
    // Whether it instead becomes an honest unpaid/pending registration is the
    // #233 product decision; either way it does not go through a fake terminal.
    if (activitySignupIsServerAuthoritative()) {
      setError(t('mobile.activities.paymentNotReady'));
      return Promise.resolve();
    }
    setPayFor(a);
    return Promise.resolve();
  };

  return (
    <div className="px-4 pb-4 space-y-4">
      {/* #276: a failed save always ends in a sentence the member can act on. */}
      {error && (
        <div
          role="alert"
          className="rounded-xl px-3 py-2 text-[12px] font-semibold"
          style={{ background: '#fdecec', color: '#8a1c1c', border: '1px solid #f3c2c2' }}
        >
          {error}
        </div>
      )}
      {/* And a save that has become NOTICEABLE says so, rather than sitting silent. */}
      {slowId != null && !error && (
        <div
          role="status"
          className="rounded-xl px-3 py-2 text-[12px]"
          style={{ background: '#f2f4f0', color: '#0f1611', border: '1px solid #e7eae3' }}
        >
          {t('mobile.common.saving')}
        </div>
      )}
      <div className={`flex items-end justify-between gap-2 ${embedded ? '' : 'pt-2'}`}>
        {!embedded && (
          <h1
            className="font-display font-semibold text-ink leading-tight"
            style={{ fontSize: 26 }}
          >
            {t('mobile.activities.title')}
          </h1>
        )}
        {!embedded && onOpenCenters && (
          <button
            onClick={onOpenCenters}
            className="flex items-center gap-1 rounded-full px-3 py-1.5 text-[11px] font-semibold flex-shrink-0 max-w-[55%]"
            style={{
              background: centerFilter ? '#0f1611' : '#fff',
              color: centerFilter ? '#fff' : '#0f1611',
              border: centerFilter ? 'none' : '1px solid #e7eae3',
            }}
            aria-label={t('mobile.activities.pickCentre')}
          >
            <MapPin size={12} className="flex-shrink-0" />
            <span className="truncate">
              {centerFilter ? centerName || centerFilter : t('mobile.centres.all')}
            </span>
            <ChevronDown size={12} className="flex-shrink-0" />
          </button>
        )}
      </div>

      {atCenter.length > 0 && (
        <div className="flex items-center gap-2 -mt-1">
          <button
            onClick={() => setOnlyEligible((v) => !v)}
            aria-pressed={onlyEligible}
            className="flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[11px] font-semibold"
            style={{
              background: onlyEligible ? '#c2f02f' : '#fff',
              color: '#0f1611',
              border: `1px solid ${onlyEligible ? '#c2f02f' : '#e7eae3'}`,
            }}
          >
            <Gauge size={12} />{' '}
            {t('mobile.activities.myLevelFilter', { level: String(memberLevel) })}
          </button>
          {hiddenCount > 0 && (
            <span className="text-[11px] text-ink-3">
              {onlyEligible
                ? t('mobile.activities.hiddenCount', { count: hiddenCount })
                : t('mobile.activities.outsideLevelCount', { count: hiddenCount })}
            </span>
          )}
        </div>
      )}

      {list.length === 0 ? (
        <EmptyState
          icon={<Clock size={22} />}
          title={
            centerFilter && all.length > 0
              ? t('mobile.activities.emptyAtCentre')
              : t('mobile.activities.empty')
          }
          hint={
            centerFilter && all.length > 0
              ? t('mobile.activities.emptyAtCentreHint')
              : t('mobile.activities.emptyHint')
          }
        />
      ) : (
        // Activity cards two-up on a wide screen; the section title spans both.
        <div className="space-y-3 @wide:space-y-0 @wide:grid @wide:grid-cols-2 @wide:gap-3 @wide:items-start @wide:[&>:first-child]:col-span-2">
          <SectionTitle>{t('mobile.activities.openForSignup')}</SectionTitle>
          {list.map((a: any) => {
            const tt = getTournamentType(a.config);
            const reg = memberRegistration(a, member?.id);
            const cap = activityCapacity(a);
            const price = Number(a.config?.pricePerPlayer) || 0;
            const ok = levelOk(a);
            const simple = supportsIndividualSignup(a);
            const full = cap.max != null && cap.confirmed >= cap.max;
            const busy = busyId === a.id;
            const venue = activityVenue(a, clubInfo);
            const lvlText =
              a.config?.levelMin != null || a.config?.levelMax != null
                ? t('mobile.activities.levelRange', {
                    min: String(a.config?.levelMin ?? 1),
                    max: String(a.config?.levelMax ?? 9),
                  })
                : t('mobile.activities.allLevels');
            return (
              <Card key={a.id} accent={tt.color}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="flex items-center gap-1.5 mb-0.5">
                      <span
                        className="font-mono text-[9px] font-bold px-1.5 py-0.5 rounded"
                        style={{ background: tt.color + '33', color: '#0f1611' }}
                      >
                        {tt.abbrev}
                      </span>
                      <span className="font-mono text-[10px] uppercase tracking-wider text-ink-3 truncate">
                        {activityWhenLabel(a, bookings)}
                      </span>
                    </div>
                    <div
                      className="font-display font-semibold text-ink leading-tight"
                      style={{ fontSize: 18 }}
                    >
                      {a.config?.name || tt.label}
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <div className="font-semibold text-ink text-[15px]">
                      {price > 0 ? `${price} kr` : t('mobile.activities.free')}
                    </div>
                    <div className="text-[10px] text-ink-3">{t('mobile.activities.perPlayer')}</div>
                  </div>
                </div>

                <div className="flex items-center gap-3 mt-2.5 text-[11px] text-ink-2 flex-wrap">
                  <span className="flex items-center gap-1">
                    <Users size={12} />
                    {cap.confirmed}
                    {cap.max != null ? `/${cap.max}` : ''}
                    {cap.waitlist > 0 && (
                      <span className="text-ink-3">
                        {t('mobile.activities.onWaitlistCount', { count: cap.waitlist })}
                      </span>
                    )}
                  </span>
                  <span className={`flex items-center gap-1 ${ok ? '' : 'text-rose'}`}>
                    <Gauge size={12} /> {lvlText}
                  </span>
                  <span className="flex items-center gap-1 text-ink-3 truncate">
                    <MapPin size={12} /> {venue}
                  </span>
                </div>

                <div className="mt-3">
                  {reg ? (
                    <div className="flex items-center gap-2">
                      <span
                        className="flex-1 flex items-center gap-1.5 rounded-xl px-3 py-2 text-[13px] font-semibold"
                        style={{
                          background: reg.status === 'waitlist' ? '#fef9c3' : '#f7fdde',
                          color: '#0f1611',
                        }}
                      >
                        <Check size={15} strokeWidth={2.5} />
                        {reg.status === 'waitlist'
                          ? t('mobile.activities.onWaitlist')
                          : t('mobile.home.youAreSignedUp')}
                      </span>
                      <button
                        onClick={() => signOff(a)}
                        disabled={busy}
                        className="rounded-xl px-3 py-2 text-[12px] font-semibold disabled:opacity-40"
                        style={{
                          background: '#fff',
                          color: '#be123c',
                          border: '1px solid #fecdd3',
                        }}
                      >
                        {t('mobile.activities.signOff')}
                      </button>
                    </div>
                  ) : !simple ? (
                    <div
                      className="rounded-xl px-3 py-2 text-[12px] text-ink-2"
                      style={{ background: '#eef0ea' }}
                    >
                      {t('mobile.activities.partnerSignup')}
                    </div>
                  ) : !ok ? (
                    <div
                      className="rounded-xl px-3 py-2 text-[12px]"
                      style={{ background: '#ffe4e6', color: '#be123c' }}
                    >
                      {t('mobile.centre.outsideLevel', { level: String(memberLevel) })}
                    </div>
                  ) : (
                    <button
                      onClick={() => onSignUpClick(a)}
                      disabled={busy}
                      className="w-full rounded-xl py-2.5 text-[13px] font-semibold disabled:opacity-40 active:scale-[0.98] transition-transform"
                      style={{
                        background: full ? '#fff' : '#0f1611',
                        color: full ? '#0f1611' : '#fff',
                        border: full ? '1px solid #e7eae3' : 'none',
                      }}
                    >
                      {busy
                        ? t('mobile.activities.signingUp')
                        : full
                          ? t('mobile.activities.joinWaitlist')
                          : price > 0
                            ? t('mobile.activities.signUpPriced', { price: String(price) })
                            : t('mobile.home.signUp')}
                    </button>
                  )}
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {payFor && (
        <BookingPaymentModal
          amount={Number(payFor.config?.pricePerPlayer) || 0}
          bookingSummary={`${payFor.config?.name || 'Aktivitet'} · ${activityWhenLabel(payFor, bookings)}`}
          onCancel={() => setPayFor(null)}
          onSuccess={(receipt: any) => {
            const a = payFor;
            setPayFor(null);
            signUp(a, receipt);
          }}
        />
      )}
    </div>
  );
}
